import React, { useEffect, useState } from "react";
import DayPicker, { DayModifiers } from "react-day-picker";
import dayjs from "dayjs";
import { todayInSydney } from "../../../common/clock";
import { isBeforeToday, isWeekend, toIsoDate } from "../../../common/util";
import { RentalDatePickerDay } from "./RentalDatePickerDay";
import { ServerGateway } from "../../ServerGateway";
import { DeliveryCalendarQuery, DeliveryCalendarResult } from "../../../common/interfaces";
import { Panel } from "@glamcorner/components/lib/core/components/Panel";
import { Header } from "@glamcorner/components/lib/core/components/Header";
import "./glamcorner.datepick.scss";

interface RentalDatePickerProps {
  header?: string;
  isDressAvailable: Function;
  onDatePicked: Function;
  postcode?: string;
  rentalPeriod?: number;
  onClose?: () => void;
}

export const RentalDatePicker: React.FC<RentalDatePickerProps> = props => {
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState<string[]>([]);
  const [highlightedRentalDays, setHighlightedRentalDays] = useState<{ from: Date; to: Date } | undefined>(undefined);
  const rentalPeriod = props.rentalPeriod || 4;

  const isDateDisabled = (date: Date) => {
    if (!props.isDressAvailable(date)) {
      return true;
    }

    if (availableDeliveryDates && availableDeliveryDates.length > 0) {
      const isoDate = toIsoDate(date);
      const inDeliveryCalendar = availableDeliveryDates.indexOf(isoDate) > -1;
      return !inDeliveryCalendar;
    } else {
      return isBeforeToday(date) || isWeekend(date);
    }
  };

  const renderDay = (date: Date, modifiers: DayModifiers) => {
    return <RentalDatePickerDay date={date} modifiers={modifiers} />;
  };

  const isFirstHighlightedDay = (date: Date) => {
    if (!highlightedRentalDays) {
      return false;
    }
    return dayjs(date).isSame(highlightedRentalDays.from, "day");
  };

  const isLastHighlightedDay = (date: Date) => {
    if (!highlightedRentalDays) {
      return false;
    }
    return dayjs(date).isSame(highlightedRentalDays.to, "day");
  };

  const clearHighlight = () => {
    setHighlightedRentalDays(undefined);
  };

  const handleOnDayMouseEnter = (date: Date, modifier: DayModifiers) => {
    if (modifier.disabled) {
      clearHighlight();
    } else {
      setHighlightedRentalDays({
        from: date,
        to: dayjs(date)
          .add(rentalPeriod - 1, "day")
          .toDate()
      });
    }
  };

  const handleOnDayClick = (startDate: Date, modifiers: DayModifiers) => {
    if (modifiers.disabled) {
      return;
    }

    const rentalDates = {
      startDate: toIsoDate(startDate),
      endDate: toIsoDate(
        dayjs(startDate)
          .add(rentalPeriod - 1, "day")
          .toDate()
      )
    };

    props.onDatePicked(rentalDates);
    if (props.onClose) {
      props.onClose();
    }
  };

  useEffect(() => {
    const fetchDates = async () => {
      const query: DeliveryCalendarQuery = {
        postcode: props.postcode
      };

      const response: DeliveryCalendarResult | null = await new ServerGateway().deliveryCalendar(query);

      if (response) {
        setAvailableDeliveryDates(response.available_delivery_dates || []);
      }
    };
    fetchDates();
  }, [props.postcode]);

  return (
    <div className="js-rental-date-picker">
      <Panel ready={true} className="c-panel--with-header c-panel--no-border">
        <Header onClick={props.onClose}>Pick your delivery date</Header>
        <p>{props.header}</p>

        <DayPicker
          initialMonth={todayInSydney().toDate()}
          disabledDays={isDateDisabled}
          renderDay={renderDay}
          selectedDays={highlightedRentalDays}
          modifiers={{
            "selected-first": isFirstHighlightedDay,
            "selected-last": isLastHighlightedDay
          }}
          onDayMouseEnter={handleOnDayMouseEnter}
          onDayClick={handleOnDayClick}
        />

        <div className="cal_footer clearfix">
          <p className="datepicker-legend-start">
            <i>
              <i>&bull;</i>
            </i>
            <strong>At your door</strong>
            by 8pm
          </p>
          <p className="datepicker-legend-end">
            <i>
              <i>&bull;</i>
            </i>
            <strong>Drop in mail</strong>
            by 5pm
          </p>
        </div>
      </Panel>
    </div>
  );
};
